import { X } from "lucide-react";
import {
  getLocationsByIds,
  LocationWithImages,
  MarkerCoordinate,
} from "~/lib/server-functions/get-locations";
import { locationPriceLevelValues } from "~/db/schema";
import noImageFound from "~/assets/no-image.svg";
import LocalOffer from "@material-design-icons/svg/outlined/local_offer.svg?react";
import Person from "@material-design-icons/svg/outlined/person_outline.svg?react";
import LocationIcon from "@material-design-icons/svg/outlined/location_on.svg?react";
import { Link } from "@tanstack/react-router";
import { getMaxCapacity } from "~/lib/utils/location-capacity";
import { IMAGE_SIZES, buildImageUrl } from "~/lib/utils";
import { getLocationPath } from "~/lib/utils/location-path";
import { withLocale } from "~/lib/utils/with-locale";
import { useEffect, useState } from "react";

interface LocationInfoWindowContentProps {
  location: LocationWithImages;
  onClose: () => void;
}

/**
 * React component rendered inside Google Maps InfoWindow
 *
 * Displays location metadata and image - no buttons or actions needed
 * since the location data is already loaded (shown in LocationCard).
 */
export function LocationInfoWindowContent({
  location,
  onClose,
}: LocationInfoWindowContentProps) {
  const firstImagePath = location.images?.[0]?.path;
  const firstImage = firstImagePath
    ? buildImageUrl(firstImagePath, { size: IMAGE_SIZES.mapThumb })
    : noImageFound;

  return (
    <div className="w-[350px] md:w-[450px] flex gap-3">
      {/* Custom close button */}
      <button
        onClick={onClose}
        className="absolute top-1 left-1 z-10 rounded-full bg-white/90 hover:bg-white shadow-sm transition-colors"
        aria-label="Schließen"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Image */}
      <div className="relative shrink-0 w-[160px] h-[140px] rounded-t-sm overflow-hidden">
        <img
          src={firstImage}
          alt={location.name}
          className="w-full h-full object-cover"
        />
        {location.images && location.images.length > 1 && (
          <span className="absolute bottom-2 right-2 text-xs bg-black/60 text-white rounded-md px-2 py-1">
            1 / {location.images.length}
          </span>
        )}
      </div>

      {/* Content */}
      <a
        href={withLocale(getLocationPath(location))}
        target="_blank"
        rel="noopener noreferrer"
        className="pr-3 pt-1"
      >
        <h3 className="font-bold text-base mb-0.5 line-clamp-1">
          {location.name}
        </h3>

        {/* Location Types */}
        {location.locationTypes && location.locationTypes.length > 0 && (
          <div className="mb-2.5">
            <span className="text-xs text-neutral-darker line-clamp-2">
              {location.locationTypes.map((t) => t.name).join(", ")}
            </span>
          </div>
        )}

        {/* Details */}
        <dl className="space-y-1 text-xs">
          {location.priceLevel && (
            <div className="flex items-center gap-2">
              <LocalOffer className="rotate-90 w-4 h-4 text-neutral-darker shrink-0" />
              <dt className="font-medium">Miete:</dt>
              <dd className="text-neutral-darker">
                {locationPriceLevelValues[location.priceLevel] ||
                  "keine Angabe"}
              </dd>
            </div>
          )}

          {(() => {
            const maxCapacity = getMaxCapacity(location);
            return maxCapacity !== null ? (
              <div className="flex items-center gap-2">
                <Person className="w-4 h-4 text-neutral-darker shrink-0" />
                <dt className="font-medium">max. Personen:</dt>
                <dd className="text-neutral-darker">{maxCapacity}</dd>
              </div>
            ) : null;
          })()}

          {location.city && (
            <div className="flex items-center gap-2">
              <LocationIcon className="w-4 h-4 text-neutral-darker shrink-0" />
              <dd className="text-neutral-darker">{location.city}</dd>
            </div>
          )}
        </dl>
      </a>
    </div>
  );
}

interface FetchingMarkerInfoWindowContentProps {
  marker: MarkerCoordinate;
  onClose: () => void;
}

/**
 * Wrapper that shows a loading skeleton while fetching full location data,
 * then renders the standard LocationInfoWindowContent once loaded.
 */
export function FetchingMarkerInfoWindowContent({
  marker,
  onClose,
}: FetchingMarkerInfoWindowContentProps) {
  const [location, setLocation] = useState<LocationWithImages | null>(null);

  useEffect(() => {
    let cancelled = false;
    getLocationsByIds({ data: [marker.id] }).then((locations) => {
      if (!cancelled && locations.length > 0) {
        setLocation(locations[0]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [marker.id]);

  if (location) {
    return <LocationInfoWindowContent location={location} onClose={onClose} />;
  }

  return (
    <div className="w-[350px] md:w-[450px] flex gap-3">
      <button
        onClick={onClose}
        className="absolute top-1 left-1 z-10 rounded-full bg-white/90 hover:bg-white shadow-sm transition-colors"
        aria-label="Schließen"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="shrink-0 w-[160px] h-[120px] rounded-t-sm bg-neutral-200 animate-pulse" />
      <div className="pr-3 pt-1 flex-1">
        <div className="h-5 w-3/4 bg-neutral-200 rounded animate-pulse mb-2" />
        <div className="h-3 w-1/2 bg-neutral-200 rounded animate-pulse mb-3" />
        <div className="space-y-2">
          <div className="h-3 w-2/3 bg-neutral-200 rounded animate-pulse" />
          <div className="h-3 w-1/2 bg-neutral-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}
